import React from "react";
import { alertService } from "../../../../services/api/alertService";
import {
  parseAlertCount,
  useAlertsScopeStore,
} from "../AlertsStore/alertsScopeStore";
import {
  diffAlertCounts,
  OtherAlertsCount,
  replaceGroupId,
  resolveAlertScope,
  ResolvedAlertScope,
  ScopeGroup,
} from "./alertScope";

/**
 * Derives the applied group scope and, when that group is nested, loads the
 * account-root alert count over the same search string with group_id swapped.
 * alertService dedupes an in-flight URL, so the filter bar and the table can
 * both call this without issuing two requests.
 */
export const useAlertScopeView = ({
  enabled,
  parentAccounts,
  appliedGroupId,
}: {
  enabled: boolean;
  parentAccounts: ScopeGroup[];
  appliedGroupId: string | null;
}): {
  scope: ResolvedAlertScope | null;
  other: OtherAlertsCount | null;
  minStartTimestamp: number | null;
} => {
  const scope = enabled
    ? resolveAlertScope(parentAccounts, appliedGroupId)
    : null;
  const groupSnapshot = useAlertsScopeStore((state) => state.groupSnapshot);
  const accountSnapshot = useAlertsScopeStore((state) => state.accountSnapshot);
  const setAccountSnapshot = useAlertsScopeStore(
    (state) => state.setAccountSnapshot
  );

  const accountRootId = scope?.narrower ? scope.accountRoot?.id ?? null : null;
  const groupMatches =
    Boolean(groupSnapshot) &&
    groupSnapshot?.groupId === String(appliedGroupId ?? "");

  React.useEffect(() => {
    if (!enabled || accountRootId == null || !groupSnapshot || !groupMatches) {
      return undefined;
    }
    let cancelled = false;
    const searchParamsString = replaceGroupId(
      groupSnapshot.searchParamsString,
      accountRootId
    );
    const filterType = groupSnapshot.filterType;
    alertService
      .getAlerts({ filterType, searchParamsString })
      .then((response) => {
        if (cancelled) {
          return;
        }
        setAccountSnapshot({
          groupId: String(accountRootId),
          filterType,
          searchParamsString,
          ...parseAlertCount(response),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAccountSnapshot(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, accountRootId, groupMatches, groupSnapshot, setAccountSnapshot]);

  const accountCount =
    accountRootId != null &&
    accountSnapshot &&
    accountSnapshot.groupId === String(accountRootId)
      ? accountSnapshot
      : null;

  const other =
    scope?.narrower && accountCount && groupSnapshot && groupMatches
      ? diffAlertCounts(
          accountCount.itemCount,
          groupSnapshot.itemCount,
          accountCount.limited,
          groupSnapshot.limited
        )
      : null;

  return {
    scope,
    other,
    minStartTimestamp:
      groupMatches && groupSnapshot ? groupSnapshot.minStartTimestamp : null,
  };
};
